import { Fixture as FixtureNg, FixtureTimelineEvent } from "../TeamOverview/TeamOverviewModels";
import {
    THE_GAME_MANIFEST_KEY,
    THE_GAME_STANDINGS_KEY,
    TheGameManifest,
    TheGameManifestMarket,
    TheGameRoundEntry,
    TheGameStandings,
    TheGameStandingsMarket
} from "../CreateTheGameRound/TheGameModels";

export const MARKET_MATCH_ODDS_FT = 1;
export const MARKET_MATCH_ODDS_HT = 2;
export const MARKET_OVER_UNDER_FT = 3;
export const MARKET_OVER_UNDER_HT = 4;
export const MARKET_PLAYER_SCORES_FT = 5;
export const MARKET_CORNERS_OVER_UNDER_FT = 6;
export const MARKET_CARDS_OVER_UNDER_FT = 7;

export const SIGN_ONE = "1";
export const SIGN_X = "X";
export const SIGN_TWO = "2";

export const EDITABLE_ROUND_STATUSES = ["ACTIVE", "PAUSED"];
export const FINISHED_STATUS = "FINISHED";
export const MAX_NOTE_LENGTH = 100;

export const GOAL = "GOAL";
export const PENALTY = "PENALTY";
export const OWN_GOAL = "OWN_GOAL";
export const YELLOW_CARD = "YELLOW_CARD";
export const YELLOW_RED = "YELLOW_RED";
export const RED_CARD = "RED_CARD";
export const HALF_TIME = "HALF_TIME";
export const FULL_TIME = "FULL_TIME";
export const FINISHED_AFTER_EXTRA = "FINISHED_AFTER_EXTRA";
export const KICKOFF = "KICKOFF";

// Markers carry no minute, team or player.
export const MARKER_TYPES = [KICKOFF, HALF_TIME, FULL_TIME, FINISHED_AFTER_EXTRA];
export const MANUAL_EVENT_TYPES = [KICKOFF, GOAL, PENALTY, OWN_GOAL, "MISSED_PENALTY", YELLOW_CARD, YELLOW_RED, RED_CARD,
    "SUBSTITUTION", "VAR", HALF_TIME, FULL_TIME];

const MARKER_SORT_KEYS: Record<string, number> = {
    [KICKOFF]: -1,
    // After 45+x stoppage time, before minute 46.
    [HALF_TIME]: 45.99,
    [FULL_TIME]: 90.99,
    [FINISHED_AFTER_EXTRA]: 999
};

// Same regex as bogateway-ng TheGameServiceImpl.NOTE_SCORE.
const NOTE_SCORE = /^\s*(\d{1,2})\s*-\s*(\d{1,2})/;
// A goal event's result is the running score (home-away) after that goal.
const RESULT_SCORE = /^\s*(\d+)\s*-\s*(\d+)\s*$/;
const GOAL_TYPES = [GOAL, PENALTY, OWN_GOAL];
const PLAYER_SCORES_LABEL_SUFFIX = / scores \(FT\)$/;

const CARD_POINTS: Record<string, number> = {
    [YELLOW_CARD]: 1,
    [YELLOW_RED]: 2,
    [RED_CARD]: 2
};

export interface PlayerRow {
    team: string;
    player: string;
    goals: number;
    yellowCards: number;
    redCards: number;
}

export interface ApplyResult {
    markets: TheGameStandingsMarket[];
    // Keyed by market pos: locked markets where the source fixture says something else.
    warnings: Record<number, string>;
}

interface Score {
    home: number;
    away: number;
}

export interface FtHtScore {
    ft: Score;
    ht: Score;
}

interface MatchSplit {
    // Events up to FULL_TIME, repeated goals removed.
    regular: FixtureTimelineEvent[];
    firstHalf: FixtureTimelineEvent[];
    fullTimeReached: boolean;
    halfTimeReached: boolean;
    ftScore: Score;
    htScore: Score;
}

export interface ImportedEvents {
    // Match order, each with an import-N id.
    events: FixtureTimelineEvent[];
    // Ids of events marked "excluded": true.
    excludedIds: string[];
}

const isOptionalString = (value: unknown) => value === null || value === undefined || typeof value === "string";
const isOptionalMinute = (value: unknown) =>
    value === null || value === undefined || (typeof value === "number" && Number.isInteger(value) && value >= 0);

const TheGameStandingsUtil = {
    parseManifest(round: TheGameRoundEntry): TheGameManifest | null {
        var value = (round.promotionValues ?? []).find(pv => pv.key === THE_GAME_MANIFEST_KEY)?.value;
        if (!value) {
            return null;
        }
        try {
            var manifest: TheGameManifest = JSON.parse(value);
            return manifest && manifest.match && Array.isArray(manifest.markets) ? manifest : null;
        } catch (error) {
            return null;
        }
    },

    // The saved standings; null when never saved (or unreadable).
    parseStandings(round: TheGameRoundEntry): TheGameStandings | null {
        var value = (round.promotionValues ?? []).find(pv => pv.key === THE_GAME_STANDINGS_KEY)?.value;
        if (!value) {
            return null;
        }
        try {
            var standings: TheGameStandings = JSON.parse(value);
            return standings && Array.isArray(standings.markets) ? standings : null;
        } catch (error) {
            return null;
        }
    },

    // Selection keys in 1, X, 2 order; JS lists integer-like keys first, so Object.keys gives 1, 2, X.
    selectionKeys(market: TheGameManifestMarket): string[] {
        var order = [SIGN_ONE, SIGN_X, SIGN_TWO];
        var rank = (key: string) => order.indexOf(key) < 0 ? order.length : order.indexOf(key);
        return Object.keys(market.selections ?? {}).sort((a, b) => rank(a) - rank(b));
    },

    isThreeWay(market: TheGameManifestMarket): boolean {
        return Object.prototype.hasOwnProperty.call(market.selections ?? {}, SIGN_TWO);
    },

    signOf(home: number, away: number): string {
        return home > away ? SIGN_ONE : home === away ? SIGN_X : SIGN_TWO;
    },

    // Returns an error message when a note score on a 3-way market contradicts the sign.
    checkNoteScore(market: TheGameManifestMarket, entry: TheGameStandingsMarket): string | null {
        if (!TheGameStandingsUtil.isThreeWay(market) || !entry.note) {
            return null;
        }
        var match = NOTE_SCORE.exec(entry.note);
        if (!match) {
            return null;
        }
        var expected = TheGameStandingsUtil.signOf(parseInt(match[1]), parseInt(match[2]));
        if (expected !== entry.sign) {
            return `Note score ${match[1]}-${match[2]} means ${expected}, not ${entry.sign}`;
        }
        return null;
    },

    normalizeNote(note: string | null | undefined): string | null {
        var trimmed = (note ?? "").trim();
        return trimmed.length > 0 ? trimmed : null;
    },

    toMatchOrder(events: FixtureTimelineEvent[] | null | undefined): FixtureTimelineEvent[] {
        return (events ?? []).slice().reverse();
    },

    matchSortKey(event: FixtureTimelineEvent): number {
        var marker = MARKER_SORT_KEYS[event.type];
        if (marker !== undefined) {
            return marker;
        }
        return (event.minute ?? 0) + Math.min(event.extraMinute ?? 0, 98) / 100;
    },

    // Manual events in match order (stable for equal minutes).
    sortManualEvents(events: FixtureTimelineEvent[]): FixtureTimelineEvent[] {
        return events.map((event, i) => ({ event, i }))
            .sort((a, b) => TheGameStandingsUtil.matchSortKey(a.event) - TheGameStandingsUtil.matchSortKey(b.event) || a.i - b.i)
            .map(it => it.event);
    },

    // Accepts the Export format (array in match order) or a whole fixture JSON (timelineEvents, newest first).
    // Throws an Error with a user-facing message when the JSON is invalid.
    parseImportedEvents(text: string): ImportedEvents {
        var parsed: any;
        try {
            parsed = JSON.parse(text);
        } catch (error) {
            throw new Error(`Invalid JSON: ${(error as Error).message}`);
        }
        var list: any[];
        if (Array.isArray(parsed)) {
            list = parsed;
        } else if (parsed && Array.isArray(parsed.timelineEvents)) {
            list = parsed.timelineEvents.slice().reverse();
        } else {
            throw new Error("Expected an array of events or a fixture with timelineEvents.");
        }
        var excludedIds: string[] = [];
        var events = list.map((item, i) => {
            var at = `Event ${i + 1}`;
            if (!item || typeof item !== "object" || typeof item.type !== "string" || item.type.trim().length === 0) {
                throw new Error(`${at}: type is required.`);
            }
            if (!isOptionalMinute(item.minute) || !isOptionalMinute(item.extraMinute)) {
                throw new Error(`${at}: minute and extraMinute must be whole numbers >= 0 or null.`);
            }
            if (item.team !== null && item.team !== undefined && item.team !== "HOME" && item.team !== "AWAY") {
                throw new Error(`${at}: team must be HOME, AWAY or null.`);
            }
            if (!isOptionalString(item.playerName) || !isOptionalString(item.relatedPlayerName) || !isOptionalString(item.result)) {
                throw new Error(`${at}: playerName, relatedPlayerName and result must be text or null.`);
            }
            var id = `import-${i + 1}`;
            if (item.excluded === true) {
                excludedIds.push(id);
            }
            var event: FixtureTimelineEvent = {
                id: id,
                type: item.type.trim(),
                minute: item.minute ?? null,
                extraMinute: item.extraMinute ?? null,
                team: item.team ?? null,
                playerName: item.playerName ?? null,
                relatedPlayerName: item.relatedPlayerName ?? null,
                result: item.result ?? null
            };
            return event;
        });
        return { events: events, excludedIds: excludedIds };
    },

    // Stable per-event keys for a match-ordered list; feed events have no id, so they are keyed by content.
    eventKeys(events: FixtureTimelineEvent[]): string[] {
        var seen: Record<string, number> = {};
        return events.map(e => {
            var base = e.id ?? [e.type, e.minute ?? "", e.extraMinute ?? "", e.team ?? "", e.playerName ?? "", e.result ?? ""].join("|");
            seen[base] = (seen[base] ?? 0) + 1;
            return `${base}#${seen[base]}`;
        });
    },

    // The fixture without the excluded events (keys from eventKeys over match order). Once a goal is excluded the
    // goal results and the fixture score no longer add up, so the score is counted from the remaining goals.
    withoutExcluded(source: FixtureNg, excluded: string[]): FixtureNg {
        if (excluded.length === 0) {
            return source;
        }
        var events = TheGameStandingsUtil.toMatchOrder(source.timelineEvents);
        var keys = TheGameStandingsUtil.eventKeys(events);
        var kept = events.filter((e, i) => !excluded.includes(keys[i]));
        var goalExcluded = events.some((e, i) => excluded.includes(keys[i]) && GOAL_TYPES.includes(e.type));
        if (!goalExcluded) {
            return { ...source, timelineEvents: kept.reverse() };
        }
        var counted = TheGameStandingsUtil.withoutRepeatedGoals(kept)
            .map(e => GOAL_TYPES.includes(e.type) ? { ...e, result: null } : e);
        var fullTimeIdx = counted.findIndex(e => e.type === FULL_TIME);
        var score = TheGameStandingsUtil.countGoals(fullTimeIdx >= 0 ? counted.slice(0, fullTimeIdx) : counted);
        return {
            ...source,
            homeTeam: { ...source.homeTeam, score: score.home },
            awayTeam: { ...source.awayTeam, score: score.away },
            timelineEvents: counted.slice().reverse()
        };
    },

    // The source fixture as if the feed had sent the manual events; the score follows the goals up to FULL_TIME.
    withManualEvents(source: FixtureNg, manualEvents: FixtureTimelineEvent[]): FixtureNg {
        var sorted = TheGameStandingsUtil.sortManualEvents(manualEvents);
        var fullTimeIdx = sorted.findIndex(e => e.type === FULL_TIME);
        var regular = fullTimeIdx >= 0 ? sorted.slice(0, fullTimeIdx) : sorted;
        var score = TheGameStandingsUtil.countGoals(TheGameStandingsUtil.withoutRepeatedGoals(regular));
        return {
            ...source,
            homeTeam: { ...source.homeTeam, score: score.home },
            awayTeam: { ...source.awayTeam, score: score.away },
            timelineEvents: sorted.slice().reverse()
        };
    },

    formatMinute(event: FixtureTimelineEvent): string {
        if (event.minute === null || event.minute === undefined) {
            return "";
        }
        return event.extraMinute ? `${event.minute}+${event.extraMinute}` : `${event.minute}`;
    },

    teamName(event: FixtureTimelineEvent, fixture: FixtureNg): string {
        if (event.team === "HOME") {
            return fixture.homeTeam?.name ?? "HOME";
        }
        if (event.team === "AWAY") {
            return fixture.awayTeam?.name ?? "AWAY";
        }
        return "";
    },

    getPlayerRows(fixture: FixtureNg): PlayerRow[] {
        var rows = new Map<string, PlayerRow>();
        var events = TheGameStandingsUtil.withoutRepeatedGoals(TheGameStandingsUtil.toMatchOrder(fixture.timelineEvents));
        events.forEach(event => {
            var player = event.playerName?.trim();
            if (!player || ![GOAL, YELLOW_CARD, YELLOW_RED, RED_CARD].includes(event.type)) {
                return;
            }
            var team = TheGameStandingsUtil.teamName(event, fixture);
            var key = `${team}|${player.toLowerCase()}`;
            var row = rows.get(key);
            if (!row) {
                row = { team: team, player: player, goals: 0, yellowCards: 0, redCards: 0 };
                rows.set(key, row);
            }
            if (event.type === GOAL) {
                row.goals++;
            }
            if (event.type === YELLOW_CARD || event.type === YELLOW_RED) {
                row.yellowCards++;
            }
            if (event.type === RED_CARD || event.type === YELLOW_RED) {
                row.redCards++;
            }
        });
        return Array.from(rows.values()).sort((a, b) => a.team.localeCompare(b.team) || a.player.localeCompare(b.player));
    },

    // The feed can repeat a goal (e.g. around a VAR check) with the same running score; keep the first one.
    withoutRepeatedGoals(events: FixtureTimelineEvent[]): FixtureTimelineEvent[] {
        var lastResult: string | null = null;
        return events.filter(event => {
            if (!GOAL_TYPES.includes(event.type)) {
                return true;
            }
            var result = event.result?.replace(/\s/g, "") || null;
            if (result !== null && result === lastResult) {
                return false;
            }
            lastResult = result;
            return true;
        });
    },

    countGoals(events: FixtureTimelineEvent[]): Score {
        var goals = events.filter(event => GOAL_TYPES.includes(event.type));
        var lastResult = goals.length > 0 ? RESULT_SCORE.exec(goals[goals.length - 1].result ?? "") : null;
        if (lastResult) {
            return { home: parseInt(lastResult[1]), away: parseInt(lastResult[2]) };
        }
        var score: Score = { home: 0, away: 0 };
        goals.forEach(event => {
            if (!event.team) {
                return;
            }
            var forHome: boolean;
            if (event.type === GOAL || event.type === PENALTY) {
                forHome = event.team === "HOME";
            } else if (event.type === OWN_GOAL) {
                forHome = event.team === "AWAY";
            } else {
                return;
            }
            if (forHome) {
                score.home++;
            } else {
                score.away++;
            }
        });
        return score;
    },

    splitMatch(source: FixtureNg): MatchSplit {
        var events = TheGameStandingsUtil.withoutRepeatedGoals(TheGameStandingsUtil.toMatchOrder(source.timelineEvents));
        var fullTimeIdx = events.findIndex(e => e.type === FULL_TIME);
        var regular = fullTimeIdx >= 0 ? events.slice(0, fullTimeIdx) : events;
        var halfTimeIdx = regular.findIndex(e => e.type === HALF_TIME);
        var firstHalf = halfTimeIdx >= 0 ? regular.slice(0, halfTimeIdx) : regular;
        var sourceFinished = source.status === FINISHED_STATUS;
        return {
            regular: regular,
            firstHalf: firstHalf,
            fullTimeReached: sourceFinished || fullTimeIdx >= 0,
            halfTimeReached: sourceFinished || halfTimeIdx >= 0,
            ftScore: events.some(e => e.type === FINISHED_AFTER_EXTRA)
                ? TheGameStandingsUtil.countGoals(regular)
                : { home: source.homeTeam?.score ?? 0, away: source.awayTeam?.score ?? 0 },
            htScore: TheGameStandingsUtil.countGoals(firstHalf)
        };
    },

    sumByTeam(events: FixtureTimelineEvent[], weight: (event: FixtureTimelineEvent) => number): Score {
        var score: Score = { home: 0, away: 0 };
        events.forEach(event => {
            if (event.team === "HOME") {
                score.home += weight(event);
            } else if (event.team === "AWAY") {
                score.away += weight(event);
            }
        });
        return score;
    },

    // Cards are card points (as in the Cards O/U market); yellow and red cards are event counts.
    getFilterScores(source: FixtureNg): Record<"GOALS" | "CARDS" | "YELLOW" | "RED", FtHtScore> {
        var split = TheGameStandingsUtil.splitMatch(source);
        var both = (weight: (event: FixtureTimelineEvent) => number): FtHtScore => ({
            ft: TheGameStandingsUtil.sumByTeam(split.regular, weight),
            ht: TheGameStandingsUtil.sumByTeam(split.firstHalf, weight)
        });
        return {
            GOALS: { ft: split.ftScore, ht: split.htScore },
            CARDS: both(e => CARD_POINTS[e.type] ?? 0),
            YELLOW: both(e => e.type === YELLOW_CARD ? 1 : 0),
            RED: both(e => e.type === RED_CARD || e.type === YELLOW_RED ? 1 : 0)
        };
    },

    // FT and HT score from the events only (up to FULL_TIME), never from the fixture score.
    getEventScore(source: FixtureNg): FtHtScore {
        var split = TheGameStandingsUtil.splitMatch(source);
        return { ft: TheGameStandingsUtil.countGoals(split.regular), ht: split.htScore };
    },

    formatFtHt(score: FtHtScore): string {
        return `${score.ft.home}-${score.ft.away} (${score.ht.home}-${score.ht.away})`;
    },

    // Running goal total: the goal's result when present, else one more.
    nextGoalTotal(running: number, event: FixtureTimelineEvent): number {
        if (!GOAL_TYPES.includes(event.type)) {
            return running;
        }
        var result = RESULT_SCORE.exec(event.result ?? "");
        return result ? parseInt(result[1]) + parseInt(result[2]) : running + 1;
    },

    // Minute of the event that first takes the running total past the line, as e.g. "(67')".
    decidingMinute(events: FixtureTimelineEvent[], line: number,
                   next: (running: number, event: FixtureTimelineEvent) => number): string | null {
        var running = 0;
        for (var i = 0; i < events.length; i++) {
            running = next(running, events[i]);
            if (running > line) {
                var minute = TheGameStandingsUtil.formatMinute(events[i]);
                return minute ? `(${minute}')` : null;
            }
        }
        return null;
    },

    // Keyed by pos: match odds / over-under goals markets whose sign or note score disagrees with the source score.
    // HT markets are only checked when there are events, since the HT score comes from them.
    getScoreConflicts(manifest: TheGameManifest, entries: TheGameStandingsMarket[], source: FixtureNg): Record<number, string> {
        var { ftScore, htScore } = TheGameStandingsUtil.splitMatch(source);
        var hasEvents = (source.timelineEvents ?? []).length > 0;
        var conflicts: Record<number, string> = {};
        entries.forEach(entry => {
            var market = manifest.markets.find(m => m.pos === entry.pos);
            if (!market) {
                return;
            }
            var isHt = market.marketTypeId === MARKET_MATCH_ODDS_HT || market.marketTypeId === MARKET_OVER_UNDER_HT;
            if (isHt && !hasEvents) {
                return;
            }
            var score = isHt ? htScore : ftScore;
            var scoreText = `${score.home}-${score.away}${isHt ? " HT" : ""}`;
            var problems: string[] = [];
            if (market.marketTypeId === MARKET_MATCH_ODDS_FT || market.marketTypeId === MARKET_MATCH_ODDS_HT) {
                var expected = TheGameStandingsUtil.signOf(score.home, score.away);
                if (entry.sign !== expected) {
                    problems.push(`source ${scoreText} means ${expected}, sign is ${entry.sign}`);
                }
                var noteScore = NOTE_SCORE.exec(entry.note ?? "");
                if (noteScore && (parseInt(noteScore[1]) !== score.home || parseInt(noteScore[2]) !== score.away)) {
                    problems.push(`note says ${noteScore[1]}-${noteScore[2]}, source is ${scoreText}`);
                }
            } else if (market.marketTypeId === MARKET_OVER_UNDER_FT || market.marketTypeId === MARKET_OVER_UNDER_HT) {
                var total = score.home + score.away;
                var expectedOu = total > (market.line ?? 0) ? SIGN_ONE : SIGN_X;
                if (entry.sign !== expectedOu) {
                    problems.push(`source ${scoreText} (${total} goals) means ${expectedOu}, sign is ${entry.sign}`);
                }
            }
            if (problems.length > 0) {
                conflicts[entry.pos] = `Conflict: ${problems.join("; ")}`;
            }
        });
        return conflicts;
    },

    // Works out sign, decided and note per market from the source fixture; corners and locked markets are kept.
    applySourceFixture(manifest: TheGameManifest, current: TheGameStandingsMarket[], source: FixtureNg,
                       roundFixtures: FixtureNg[]): ApplyResult {
        var { regular, firstHalf, fullTimeReached, halfTimeReached, ftScore, htScore } = TheGameStandingsUtil.splitMatch(source);
        var cards = regular.reduce((sum, e) => sum + (CARD_POINTS[e.type] ?? 0), 0);
        var nextCards = (running: number, e: FixtureTimelineEvent) => running + (CARD_POINTS[e.type] ?? 0);

        // Decided before the period ends -> the note gets the minute of the deciding event.
        var overUnder = (market: TheGameManifestMarket, count: number, reached: boolean, unit: string,
                         events: FixtureTimelineEvent[], next: (running: number, e: FixtureTimelineEvent) => number) => {
            var line = market.line ?? 0;
            var over = count > line;
            var minute = over && !reached ? TheGameStandingsUtil.decidingMinute(events, line, next) : null;
            return { sign: over ? SIGN_ONE : SIGN_X, decided: over || reached, note: `${count} ${unit}${minute ? ` ${minute}` : ""}` };
        };

        var warnings: Record<number, string> = {};
        var markets = current.map(entry => {
            var market = manifest.markets.find(m => m.pos === entry.pos);
            if (!market) {
                return entry;
            }
            var derived: { sign: string, decided: boolean, note: string | null } | null = null;
            switch (market.marketTypeId) {
                case MARKET_MATCH_ODDS_FT:
                    derived = {
                        sign: TheGameStandingsUtil.signOf(ftScore.home, ftScore.away),
                        decided: fullTimeReached,
                        note: `${ftScore.home}-${ftScore.away}`
                    };
                    break;
                case MARKET_MATCH_ODDS_HT:
                    derived = {
                        sign: TheGameStandingsUtil.signOf(htScore.home, htScore.away),
                        decided: halfTimeReached,
                        note: `${htScore.home}-${htScore.away} HT`
                    };
                    break;
                case MARKET_OVER_UNDER_FT:
                    derived = overUnder(market, ftScore.home + ftScore.away, fullTimeReached, "goals", regular,
                        TheGameStandingsUtil.nextGoalTotal);
                    break;
                case MARKET_OVER_UNDER_HT:
                    derived = overUnder(market, htScore.home + htScore.away, halfTimeReached, "goals", firstHalf,
                        TheGameStandingsUtil.nextGoalTotal);
                    break;
                case MARKET_CARDS_OVER_UNDER_FT:
                    derived = overUnder(market, cards, fullTimeReached, "cards", regular, nextCards);
                    break;
                case MARKET_PLAYER_SCORES_FT: {
                    var player = market.label.replace(PLAYER_SCORES_LABEL_SUFFIX, "").trim().toLowerCase();
                    var playerGoals = regular.filter(e => e.type === GOAL && e.playerName?.trim().toLowerCase() === player);
                    var goals = playerGoals.length;
                    var firstGoal = goals > 0 && !fullTimeReached ? TheGameStandingsUtil.formatMinute(playerGoals[0]) : "";
                    var scored = goals === 1 ? "scored" : `scored ${goals}`;
                    derived = {
                        sign: goals > 0 ? SIGN_ONE : SIGN_X,
                        decided: goals > 0 || fullTimeReached,
                        note: goals === 0 ? null : firstGoal ? `${scored} (${firstGoal}')` : scored
                    };
                    break;
                }
            }
            if (!derived) {
                return entry;
            }
            if (roundFixtures[entry.pos]?.status === FINISHED_STATUS) {
                if (derived.sign !== entry.sign || derived.decided !== entry.decided) {
                    warnings[entry.pos] = `Locked, but the source fixture says ${derived.sign}${derived.decided ? " (decided)" : ""}`;
                }
                return entry;
            }
            return { pos: entry.pos, sign: derived.sign, decided: derived.decided, note: derived.note };
        });
        return { markets: markets, warnings: warnings };
    }
};

export default TheGameStandingsUtil;
